import { useMemo, useState } from 'react';
import { Modal, Button, Loader, TextInput, ScrollArea, Group, Stack, Text, Box } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { generatePrompt } from '@mygpt/app/robots/list/services/agent';
import { computeSideBySideDiff, type DiffSegment } from './diff-utils';

interface OptimizationModalProps {
  opened: boolean;
  onClose: () => void;
  currentPrompt: string;
  promptType?: 'main' | 'realtime';
  onApply: (newPrompt: string, changeSummary?: string) => Promise<void>;
}

export const OptimizationModal = ({ opened, onClose, currentPrompt, onApply }: OptimizationModalProps) => {
  const { t } = useTranslation();
  const [optimizationInput, setOptimizationInput] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const diff = useMemo(() => {
    if (!generatedPrompt) return null;
    return computeSideBySideDiff(currentPrompt, generatedPrompt);
  }, [currentPrompt, generatedPrompt]);

  const handleGenerate = async () => {
    if (!optimizationInput.trim()) {
      showNotification({
        message: t('errors.prompt_cannot_be_empty'),
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await generatePrompt({
        prompt_in: optimizationInput,
        cur_prompt: currentPrompt || null,
        stream: false,
      });
      setGeneratedPrompt(response.prompt);
      setChangeSummary(response.change_summary || '');
    } catch {
      showNotification({
        message: t('settings.prompt.generate_error'),
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!generatedPrompt || applying) return;
    setApplying(true);
    try {
      await onApply(generatedPrompt, changeSummary);
      handleClose();
    } catch {
      showNotification({
        message: t('settings.prompt.apply_error'),
        color: 'red',
      });
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    setOptimizationInput('');
    setGeneratedPrompt('');
    setChangeSummary('');
    onClose();
  };

  const renderSegments = (segments: DiffSegment[]) => {
    return segments.map((seg, idx) => {
      if (seg.type === 'unchanged') {
        return <span key={idx}>{seg.text}</span>;
      }
      if (seg.type === 'removed') {
        return (
          <span key={idx} style={{ backgroundColor: '#fecaca', color: '#dc2626', textDecoration: 'line-through' }}>
            {seg.text}
          </span>
        );
      }
      return (
        <span key={idx} style={{ backgroundColor: '#bbf7d0', color: '#16a34a' }}>
          {seg.text}
        </span>
      );
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('settings.prompt.optimization_preview_title')}
      size="xl"
      styles={{
        body: { padding: '0 24px 24px' },
      }}
    >
      <Stack gap="md">
        {/* 优化指令输入 */}
        <div>
          <Text size="sm" fw={500} mb={4}>
            {t('settings.prompt.optimization_label')}
          </Text>
          <Group gap="sm" align="flex-end">
            <TextInput
              style={{ flex: 1 }}
              placeholder={t('settings.prompt.optimization_input_placeholder')}
              value={optimizationInput}
              onChange={(e) => setOptimizationInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !loading) handleGenerate();
              }}
              disabled={loading}
            />
            <Button onClick={handleGenerate} disabled={loading || !optimizationInput.trim()}>
              {loading ? <Loader size={14} /> : t('settings.prompt.generate')}
            </Button>
          </Group>
        </div>

        {/* Diff 对比视图 */}
        {diff && (
          <>
            <Group grow>
              <Text size="sm" fw={600} ta="center" c="dimmed">
                {t('settings.prompt.before')}
              </Text>
              <Text size="sm" fw={600} ta="center" c="dimmed">
                {t('settings.prompt.after')}
              </Text>
            </Group>
            <ScrollArea h={350} style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}>
              <div style={{ display: 'flex', minHeight: '100%' }}>
                <div style={{ flex: 1, borderRight: '1px solid var(--mantine-color-gray-3)' }}>
                  {diff.leftLines.map((line, idx) => {
                    let bg = 'transparent';
                    if (line.type === 'removed') bg = '#fef2f2';
                    if (line.type === 'modified') bg = '#fefce8';
                    return (
                      <Box
                        key={idx}
                        px="xs"
                        py={2}
                        style={{
                          backgroundColor: bg,
                          fontFamily: 'monospace',
                          fontSize: 13,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          minHeight: 22,
                        }}
                      >
                        {line.segments.length > 0 ? renderSegments(line.segments) : '\u00A0'}
                      </Box>
                    );
                  })}
                </div>
                <div style={{ flex: 1 }}>
                  {diff.rightLines.map((line, idx) => {
                    let bg = 'transparent';
                    if (line.type === 'added') bg = '#f0fdf4';
                    if (line.type === 'modified') bg = '#fefce8';
                    return (
                      <Box
                        key={idx}
                        px="xs"
                        py={2}
                        style={{
                          backgroundColor: bg,
                          fontFamily: 'monospace',
                          fontSize: 13,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          minHeight: 22,
                        }}
                      >
                        {line.segments.length > 0 ? renderSegments(line.segments) : '\u00A0'}
                      </Box>
                    );
                  })}
                </div>
              </div>
            </ScrollArea>

            {/* 图例 */}
            <Group gap="lg">
              <Group gap={4}>
                <Box w={10} h={10} style={{ borderRadius: '50%', backgroundColor: '#fca5a5' }} />
                <Text size="xs" c="dimmed">
                  {t('settings.prompt.legend_removed')}
                </Text>
              </Group>
              <Group gap={4}>
                <Box w={10} h={10} style={{ borderRadius: '50%', backgroundColor: '#86efac' }} />
                <Text size="xs" c="dimmed">
                  {t('settings.prompt.legend_added')}
                </Text>
              </Group>
              <Group gap={4}>
                <Box w={10} h={10} style={{ borderRadius: '50%', backgroundColor: '#fde68a' }} />
                <Text size="xs" c="dimmed">
                  {t('settings.prompt.legend_modified')}
                </Text>
              </Group>
            </Group>

            {/* 变更摘要 */}
            {changeSummary && (
              <Box
                p="md"
                style={{
                  backgroundColor: 'var(--mantine-color-gray-0)',
                  borderRadius: 8,
                  border: '1px solid var(--mantine-color-gray-2)',
                }}
              >
                <Text size="sm" fw={600} mb={4}>
                  ✨ {t('settings.prompt.change_summary_title')}
                </Text>
                <Text size="sm" c="dimmed">
                  {changeSummary}
                </Text>
              </Box>
            )}

            {/* 操作按钮 */}
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={handleClose}>
                {t('settings.prompt.cancel')}
              </Button>
              <Button onClick={handleApply} loading={applying}>
                {t('settings.prompt.apply_changes')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};
